import React, { useEffect, useRef, useState, type CSSProperties } from "react";
import { useNavigate } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { Stack, Text, Image } from "@fluentui/react";
import { Card } from "@fluentui/react-components";
import microsoftLogo from "../assets/microsoftLogo.png";
import { loginRequest } from "../auth/authConfig";
import BackButton from "./common/BackButton";
import "../styles/DeviceSelection.css";
import "../styles/common/page.css";
import "../styles/common/card-layout.css";
import { t } from "@/strings/index";
import {
  logScreenRender,
  logButtonClick,
  logBackNavigation,
  collectDeviceSelection,
  trackAuthRedirect,
  startPageRenderTiming,
  endPageRenderTiming,
} from "../telemetry/telemetryEvents";

const LIVE_REGION_DELAY_MS = 150;
const visuallyHiddenStyle: CSSProperties = {
  position: "absolute",
  width: "1px",
  height: "1px",
  margin: "-1px",
  border: 0,
  padding: 0,
  clip: "rect(0 0 0 0)",
  overflow: "hidden",
};

export default function DeviceSelection() {
  // Start timing page render immediately
  startPageRenderTiming('device-selection');
  
  const navigate = useNavigate();
  const { instance } = useMsal();
  const androidButtonRef = useRef<HTMLDivElement | null>(null);
  const liveRegionTimerRef = useRef<number | undefined>(undefined);
  const [liveMessage, setLiveMessage] = useState<string>("");

  const titleText = t("device.title");
  const androidText = t("android.title");
  const iosText = t("ios.title");

  useEffect(() => {
    // Log screen view (UserBI + marks 'device-selection' step)
    logScreenRender("DeviceSelection");
    endPageRenderTiming('device-selection');
  }, []);

  useEffect(() => {
    androidButtonRef.current?.focus();

    if (typeof window !== "undefined") {
      liveRegionTimerRef.current = window.setTimeout(() => {
        setLiveMessage(titleText);
      }, LIVE_REGION_DELAY_MS);
    }

    return () => {
      if (typeof window !== "undefined" && liveRegionTimerRef.current !== undefined) {
        window.clearTimeout(liveRegionTimerRef.current);
      }
    };
  }, [titleText]);

  const handleDeviceClick = (type: "android" | "ios") => {
    collectDeviceSelection(type);
    logButtonClick("DeviceSelection", type);
    trackAuthRedirect(true);

    // Pass selectedDevice via MSAL state (route state) instead of localStorage
    // Persist selected device in sessionStorage for AppDownloadScreen
            sessionStorage.setItem('selectedDevice', type);
            instance.loginRedirect({
      prompt: "login",
      scopes: loginRequest.scopes,
      state: JSON.stringify({ selectedDevice: type }),
    });
  };

  const handleBack = () => {
    logBackNavigation('DeviceSelection');
    navigate(-1);
  };

  return (
    <Stack className="shared-background-fullscreen" horizontalAlign="center" verticalAlign="center">
      <Stack
        as="main"
        role="main"
        className="device-selection-content"
        verticalAlign="center"
        horizontalAlign="center"
      >
        <Stack className="lightbox" tokens={{ childrenGap: 0 }}>
          <Stack horizontal verticalAlign="center" className="device-header">
            <Image
              src={microsoftLogo}
              alt="Microsoft Logo"
              className="screen-logo"
            />
          </Stack>

          <span aria-live="polite" role="status" style={visuallyHiddenStyle}>
            {liveMessage}
          </span>

          <Text variant="xLarge" className="device-title" id="device-selection-title">
            {titleText}
          </Text>

          <Stack horizontal className="device-options">
            <Card
              ref={androidButtonRef}
              onClick={() => handleDeviceClick("android")}
              className="device-card"
              aria-label={`${androidText} (1 of 2)`}
              role="button"
              tabIndex={0}
            >
              <Text variant="large" className="device-text">
                {androidText}
              </Text>
            </Card>
            <Card
              onClick={() => handleDeviceClick("ios")}
              className="device-card"
              aria-label={`${iosText} (2 of 2)`}
              role="button"
              tabIndex={0}
            >
              <Text variant="large" className="device-text">
                {iosText}
              </Text>
            </Card>
          </Stack>

          <BackButton onClick={handleBack} className="device-back-button" />
        </Stack>
      </Stack>
    </Stack>
  );
}
